import { OriginalWattwatchersMeter, WattwatchersMeter } from 'clipsal-cortex-types/src/api/api-ww-meter';

import { Assignment } from '../../../../api/api-assignments';
import { Circuit } from '../../../../api/api-device';
import {
  LONG_DATA_TYPE_TO_TITLE_MAP,
  LONG_METROLOGY_TO_LONG_DATA_KEY_MAP,
  SHORT_DATA_TYPE_TO_TITLE_MAP,
  SHORT_METROLOGY_TO_SHORT_DATA_KEY_MAP,
} from './meter-config-constants';
import {
  LongIntervalMetrologyData,
  LongPowerDataType,
  LongPowerDataTypeToSeries,
  SeriesData,
  ShortIntervalMetrologyData,
  ShortPowerDataType,
  ShortPowerDataTypeToSeries,
} from './meter-config-types';

/**
 * Converts a Wattwatchers meter object to a Clipsal meter object.
 * @meter The Wattwatchers meter object to convert.
 * @returns The Clipsal meter object.
 */
export function convertOriginalWWResponseToClipsalWWResponse(meter: OriginalWattwatchersMeter): WattwatchersMeter {
  const { comms, id, timezone, model, firmwareVersion } = meter;
  return {
    comms,
    timezone,
    model,
    firmwareVersion,
    ww_device_id: id,
    installed_device_id: null,
    circuits: [],
  };
}

// Meters can have more than 6 circuits, but we don't know until we fetch the data. Hence, we default to 6 to not break the chart.
export const DEFAULT_NUMBER_OF_CIRCUITS = 6;

// Empty Circuit data for the chart
export function buildEmptyCircuitData(length: number): SeriesData[] {
  return Array.from({ length }, () => ({
    name: '',
    data: [],
  }));
}

/**
 * Builds the chart data for short interval metrology data.
 * @param data The short interval metrology data to build the chart data from.
 * @param circuits The circuits of the meter.
 * @returns The chart data for short interval metrology data.
 */
export function buildShortMetrologyData(
  data: ShortIntervalMetrologyData[],
  circuits: Circuit[]
): ShortPowerDataTypeToSeries {
  // Mapping for each short power data type to its series data
  const chartData: ShortPowerDataTypeToSeries = Object.entries(SHORT_DATA_TYPE_TO_TITLE_MAP).reduce(
    (acc, [key, name]) => {
      acc[key as ShortPowerDataType] = {
        name,
        data: buildEmptyCircuitData(circuits.length || DEFAULT_NUMBER_OF_CIRCUITS),
      };
      return acc;
    },
    {} as ShortPowerDataTypeToSeries
  );

  if (!circuits.length) return chartData;

  data.forEach(({ readings, datetime_utc: dateTimeUTC }) => {
    // Map readings to circuits based on oem_circuit_id
    // Mainly as we can't guarantee the order of readings will match the order of circuits
    // Also there are cases where some circuits may not have readings
    const mappedReadings = circuits.map((circuit) =>
      readings.find((reading) => reading.oem_circuit_id === circuit.oem_circuit_id)
    );
    mappedReadings.forEach((reading, circuitIndex) => {
      if (!reading) return;
      (Object.keys(SHORT_DATA_TYPE_TO_TITLE_MAP) as ShortPowerDataType[]).forEach((key) => {
        const transformedKey = SHORT_METROLOGY_TO_SHORT_DATA_KEY_MAP[key];
        const series = chartData[key].data[circuitIndex];
        if (!series) return;
        if (!series.name) {
          const circuit = circuits[circuitIndex];
          series.name = `${circuit.circuit_name} (CH ${circuitIndex + 1})`;
        }

        series.data.push({
          x: new Date(dateTimeUTC).getTime(),
          y: reading[transformedKey],
        });
      });
    });
  });

  return chartData;
}

/**
 * Builds the chart data for short interval metrology data.
 * @param data The short interval metrology data to build the chart data from.
 * @param circuits The circuits of the meter.
 * @returns The chart data for short interval metrology data.
 */
export function buildLongMetrologyData(
  data: LongIntervalMetrologyData[],
  circuits: Circuit[]
): LongPowerDataTypeToSeries {
  // Mapping for each short power data type to its series data
  const chartData: LongPowerDataTypeToSeries = Object.entries(LONG_DATA_TYPE_TO_TITLE_MAP).reduce(
    (acc, [key, name]) => {
      acc[key as LongPowerDataType] = {
        name,
        data: buildEmptyCircuitData(circuits.length || DEFAULT_NUMBER_OF_CIRCUITS),
      };
      return acc;
    },
    {} as LongPowerDataTypeToSeries
  );

  if (!circuits.length) return chartData;

  data.forEach(({ readings, datetime_utc: dateTimeUTC }) => {
    // Map readings to circuits based on oem_circuit_id
    // Mainly as we can't guarantee the order of readings will match the order of circuits
    // Also there are cases where some circuits may not have readings
    const mappedReadings = circuits.map((circuit) =>
      readings.find((reading) => reading.oem_circuit_id === circuit.oem_circuit_id)
    );
    mappedReadings.forEach((reading, circuitIndex) => {
      if (!reading) return;
      (Object.keys(LONG_DATA_TYPE_TO_TITLE_MAP) as LongPowerDataType[]).forEach((key) => {
        const transformedKey = LONG_METROLOGY_TO_LONG_DATA_KEY_MAP[key];
        const series = chartData[key].data[circuitIndex];

        if (!series) return;
        if (!series.name) {
          const circuit = circuits[circuitIndex];
          series.name = `${circuit.circuit_name} (CH ${circuitIndex + 1})`;
        }

        series.data.push({
          x: new Date(dateTimeUTC).getTime(),
          y: reading[transformedKey],
        });
      });
    });
  });

  return chartData;
}

/**
 * Gets the display name for a circuit based on its assignment.
 * @param circuit The circuit to get the display name for.
 * @param assignments The assignments to check against.
 * @returns The display name for the circuit.
 */
export const getCircuitDisplayName = (circuit: Circuit, assignments: Assignment[]) => {
  const assignment = assignments.find((a) => a.assignment === circuit.clipsal_monitors);

  // If no assignment is found, return the circuit's clipsal_monitors value
  return assignment ? assignment.display_name : circuit.clipsal_monitors;
};
